import React, { useState, useMemo } from 'react';
import {
  Loader,
  Alert,
  Title,
  Text,
  Flex,
  Button,
  Label,
  Combobox,
  Legend,
  Input,
  Link,
  InputGroup,
  ValidationMessage
} from '@gjensidige/builders-components';
import { GdprComponentSe } from '@gjensidige/gdpr-component-se';
import { ErrorPage } from '@gjensidige/se-error-page';
import {
  fetchGenericIllustrations,
  IllustrationType
} from '@gjensidige/se-content-helpers';
import { CallMeFormState, DayType } from '../../types';
import './UiCallMe.scss';
import { STATUS_MESSAGE } from '../../constants/StatusMessages';

export interface UiCallMeProps {
  contactFormData: CallMeFormState;
  appText: Record<string, string>;
  isScheduledCall: boolean;
  customerSelectionOptions: {
    label: string;
    value: string;
  }[];
  sortedDaysOptions?: DayType[];
  availableTimeslots?: {
    label: string,
    value: Date
  }[];
  events: {
    onFormSubmit: () => void;
    onChangeName: (name: string) => void;
    onChangeNum: (num: string) => void;
    onChangeDay?: (day: DayType) => void;
    onChangeTime?: (time: { label: string, value: Date } | string) => void;
    onSelectCustomerRelationShip: (selection: string) => void;
  };
}


export function UiCallMe({
  contactFormData,
  appText,
  events,
  isScheduledCall,
  customerSelectionOptions,
  availableTimeslots,
  sortedDaysOptions
}: UiCallMeProps) {
  function refreshPage() {
    //@ts-ignore
    window.location.reload(true);
  }

  const inputNameId = isScheduledCall ? 'later-input-name' : 'input-name';
  const inputNumberId = isScheduledCall ? 'later-input-number' : 'input-number';

  switch (contactFormData.callMeActionStatus) {
    case 'loading': {
      return (
        <div className="spinner-container">
          <Loader size="lg" variant="spinner" />
        </div>
      )
    }
    case 'submittedFailure': {

      return (
        <ErrorPage
          primaryBtnOnClick={refreshPage}
          headingText={
            contactFormData.callMeActionStatusMessage === STATUS_MESSAGE.DUPLICATE_PHONE_NUMBER
              ? ''
              :
              appText[contactFormData.callMeActionStatusMessage]
          }
          descriptionText={
            contactFormData.callMeActionStatusMessage === STATUS_MESSAGE.DUPLICATE_PHONE_NUMBER
              ? appText[STATUS_MESSAGE.DUPLICATE_PHONE_NUMBER]
              : appText['keyFailureDesc']}
          imageSrc={fetchGenericIllustrations(
            'ExclamationErrorModal_NCI',
            IllustrationType.SIMPLE,
            [
              {
                name: 'ExclamationErrorModal_NCI',
                filename: 'ExclamationErrorModal_NCI',
                fileType: 'svg'
              }
            ]
          )}
          primaryButtonLabel={appText['keyErroButtonText']}
        />
      )
    }
    case 'submittedSuccessfully': {
      return (
        <div className="call-me-success-container">
          <Alert
            severity="success"

          >
            <Title>Tack!</Title>
            {
              isScheduledCall
                ?
                <Text>Vi kommer att ringa dig på den valda tiden.</Text>
                :
                <>
                  <Text>Vi kommer att ringa upp dig inom kort. Våra öppettider är måndag till fredag, kl. 8.30-17.</Text>
                  <Flex>
                    <Button variant="action" size="sm" arrow onClick={() => window.location.href = "/"}>
                      Till startsidan
                    </Button>
                  </Flex>
                </>
            }
          </Alert>
        </div>
      )
    }
    default: {
      return (
        <div className="call-me-container">
          <form
            id={isScheduledCall ? "call-me-later-form" : "call-me-form"}
            onSubmit={events.onFormSubmit}
            autoComplete="on"
          >
            <div className={'content-form'}>
              {isScheduledCall && (
                <>
                  {sortedDaysOptions && <div className="call-me-input">
                    <Label htmlFor="input-day">Vilken dag ska vi ringa?</Label>
                    <Combobox
                      id="input-day"
                      size={'max-content'}
                      values={contactFormData.day ? String(contactFormData.day.value) : []}
                      options={sortedDaysOptions.map(day => ({
                        value: String(day.value),
                        label: day.label
                      }))}
                      onChange={(option) => {
                        events.onChangeTime && events.onChangeTime('');
                        if (option && events.onChangeDay) {
                          const selectedDay = sortedDaysOptions.find(day => String(day.value) === option[0].value);
                          if (selectedDay) events.onChangeDay(selectedDay);
                        }
                      }}
                    />
                  </div>}
                  {availableTimeslots &&
                    <div className="call-me-input">
                      <Label htmlFor="input-call-later-time">Vilken tid passar dig?</Label>
                      <Combobox
                        id='input-call-later-time'
                        size="max-content"
                        values={contactFormData.time ? contactFormData.time.value.toISOString() : []}
                        options={availableTimeslots.map(time => ({
                          value: time.value.toISOString(),
                          label: time.label
                        }))}
                        onChange={(option) => {
                          if (option && events.onChangeTime) {
                            const selectedTime = availableTimeslots?.find(time => time.value.toISOString() === option[0].value);
                            if (selectedTime) events.onChangeTime(selectedTime);
                          }
                        }}
                      />
                    </div>}
                </>
              )}
              <div className="call-me-input">
                <Label htmlFor={inputNameId}>{appText['labelName']}</Label>
                <Input
                  wrapperProps={{ className: "customer-name-input" }}
                  id={inputNameId}
                  data-testid="input-name"
                  invalid={contactFormData.formErrors.nameInvalid}
                  required={true}
                  name="name-value"
                  autoComplete="name"
                  onBlur={(event) => { events.onChangeName(event.currentTarget.value) }}
                  onChange={(event) => { events.onChangeName(event.currentTarget.value) }}
                  value={contactFormData.name}
                />
                {contactFormData.formErrors.nameInvalid &&
                  <ValidationMessage htmlFor={inputNameId} invalid>
                    {appText['errorTextAll']}
                  </ValidationMessage>
                }
              </div>
              <div className="call-me-input">
                <Label htmlFor={inputNumberId}>{appText['labelNumber']}</Label>
                <Input
                  wrapperProps={{ className: "phone-number-input" }}
                  id={inputNumberId}
                  data-testid="input-number"
                  invalid={contactFormData.formErrors.numValidity !== 'VALID'}
                  required={true}
                  name="number-value"
                  autoComplete="tel"
                  onBlur={(event) => {
                    events.onChangeNum(event.currentTarget.value);
                  }}
                  onChange={(event) => {
                    events.onChangeNum(event.currentTarget.value);
                  }}
                  value={contactFormData.number}
                />
                {contactFormData.formErrors.numValidity !== 'VALID' &&
                  <ValidationMessage htmlFor={inputNumberId} invalid>
                    {contactFormData.formErrors.numValidity === 'EMPTY'
                      ? appText['errorTextAll']
                      : appText['keyInvalidPhone']
                    }
                  </ValidationMessage>
                }
              </div>
              <div>
                <InputGroup
                  id="input-group-radio-is-customer"
                  data-testid="input-group-radio-is-customer"
                  name='radio-customer'
                  onChange={(values: string[]) => {
                    events.onSelectCustomerRelationShip(values[0]);
                  }}
                  type="radio"
                  variant="column"
                  value={contactFormData.isCustomer}
                  required
                >
                  <Legend>{appText['labelFormHeader']}</Legend>
                  {
                    customerSelectionOptions.map((option) => {
                      return (
                        <InputGroup.Input
                          key={option.value}
                          label={option.label}
                          value={option.value}
                        />
                      )
                    })
                  }
                </InputGroup>
              </div>
            </div>
            <div className="submit-call-me-container">
              <Button
                data-testid="button-submit"
                className="submit-call-me-button"
                onClick={events.onFormSubmit}
                variant="critical"
                type="submit"
                disabled={
                  contactFormData.formErrors.nameInvalid ||
                  contactFormData.formErrors.numValidity !== 'VALID' ||
                  contactFormData.name.length === 0 ||
                  contactFormData.number.length === 0 ||
                  !contactFormData.isCustomer ||
                  contactFormData.isCustomer.length === 0 ||
                  (isScheduledCall && !contactFormData.day && !contactFormData.time)
                }
              >
                {appText['callButton']}
              </Button>
              <div className="gdpr-link">
                <GdprComponentSe />
              </div>
            </div>
          </form>
        </div>
      )
    }
  }
}

export default UiCallMe;
